import { parseContactForm, type ContactFormData } from "@/lib/contact-form-schema"

export type ContactSubmitResult =
  | { ok: true; message: string; emailId?: string; submissionId?: string }
  | { ok: false; code: "validation"; errors: Array<{ field: string; message: string }> }
  | { ok: false; code: "configuration"; message: string; submissionId?: string }
  | { ok: false; code: "delivery"; message: string; submissionId?: string }

const SUCCESS_MESSAGE = "Thank you! Your message has been sent successfully."

export function contactSubmitErrorMessage(
  result: Extract<ContactSubmitResult, { ok: false }>
): string {
  switch (result.code) {
    case "validation":
      return result.errors[0]?.message ?? "Validation failed"
    case "configuration":
      return "Email service not configured"
    case "delivery":
      return result.message
  }
}

// --- Adapter interfaces ---

export interface SubmissionStore {
  archive(data: ContactFormData): Promise<{ submissionId: string } | null>
  markDelivery(
    submissionId: string,
    update:
      | { deliveryStatus: "sent"; mailgunMessageId: string }
      | { deliveryStatus: "failed"; deliveryError: string }
  ): Promise<void>
}

export interface ContactNotifier {
  send(data: ContactFormData): Promise<{ emailId: string }>
}

// --- Default adapters (production, lazy-loaded to avoid side-effects at import time) ---

async function loadDefaultStore(): Promise<SubmissionStore> {
  const { archiveContactSubmission, markContactSubmissionDelivery } = await import(
    "@/lib/contact-submission-store"
  )
  return { archive: archiveContactSubmission, markDelivery: markContactSubmissionDelivery }
}

async function loadDefaultNotifier(): Promise<ContactNotifier> {
  const { sendContactFormNotification } = await import("@/lib/send-contact-notification")
  return { send: sendContactFormNotification }
}

// --- Orchestration ---

export async function submitContactIntake(
  raw: unknown,
  deps: { store?: SubmissionStore; notifier?: ContactNotifier } = {}
): Promise<ContactSubmitResult> {
  const parsed = parseContactForm(raw)
  if (!parsed.success) {
    return { ok: false, code: "validation", errors: parsed.errors }
  }

  const store = deps.store ?? (await loadDefaultStore())
  const notifier = deps.notifier ?? (await loadDefaultNotifier())

  // Invariant: always attempt archive before notification
  let submissionId: string | undefined

  try {
    const archived = await store.archive(parsed.data)
    submissionId = archived?.submissionId
  } catch (error) {
    console.error("Failed to archive contact submission:", error)
  }

  try {
    const { emailId } = await notifier.send(parsed.data)
    await safeMarkDelivery(store, submissionId, {
      deliveryStatus: "sent",
      mailgunMessageId: emailId,
    })
    return { ok: true, message: SUCCESS_MESSAGE, emailId, submissionId }
  } catch (error) {
    const message = error instanceof Error ? error.message : "An unexpected error occurred"
    await safeMarkDelivery(store, submissionId, {
      deliveryStatus: "failed",
      deliveryError: message,
    })

    if (error instanceof Error && error.message === "Email service not configured") {
      return { ok: false, code: "configuration", message: error.message, submissionId }
    }

    return {
      ok: false,
      code: "delivery",
      message: `Failed to send email: ${message}`,
      submissionId,
    }
  }
}

async function safeMarkDelivery(
  store: SubmissionStore,
  submissionId: string | undefined,
  update:
    | { deliveryStatus: "sent"; mailgunMessageId: string }
    | { deliveryStatus: "failed"; deliveryError: string }
): Promise<void> {
  if (!submissionId) return
  try {
    await store.markDelivery(submissionId, update)
  } catch (error) {
    console.error(`Failed to mark submission as ${update.deliveryStatus}:`, error)
  }
}

export type { ContactFormData }
